import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createTextNode, $getRoot } from 'lexical';
import {
  $createBeautifulMentionNode,
  type BeautifulMentionsItem,
  type BeautifulMentionsMenuItemProps,
  type BeautifulMentionsMenuProps,
  BeautifulMentionsPlugin,
} from 'lexical-beautiful-mentions';
import { twMerge } from 'tailwind-merge';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import type { CustomMentionNode } from '@soundxyz/utils/lexical/ContentLexicalStateV1';
import { MentionCell } from '@components/Lexical/Plugins/Mentions/MentionCell';
import { LexicalUtils } from '@components/Lexical/utils';
import {
  type ExecutionResultWithData,
  fetchQuery,
  gql,
  MentionableSearchDocument,
  type MentionableSearchQuery,
} from '@graphql/reactQuery';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { SPACE } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query MentionableSearch($input: SearchInput!) {
    search(input: $input) {
      id
      artistsPaginated(pagination: { first: 3 }) {
        edges {
          node {
            id
            name
            webappUri
            user {
              id
              publicAddress

              avatar {
                url
                id
              }
            }
          }
        }
      }
      collectors(pagination: { first: 3 }) {
        edges {
          node {
            id
            displayName
            publicAddress
            webappUri
            avatar {
              url
              id
            }
          }
        }
      }
    }
  }
`);

function dataToBeautifulMentionsItemArray(
  result: ExecutionResultWithData<MentionableSearchQuery>,
): BeautifulMentionsItem[] {
  return [
    ...result.data.search.artistsPaginated.edges.map(edge => ({
      key: `artist-${edge.node.id}`,
      value: edge.node.name,
      displayName: edge.node.name,
      publicAddress: edge.node.user.publicAddress,
      artistId: edge.node.id,
      userId: edge.node.user.id,
      avatarUrl: edge.node.user.avatar?.url || null,
      webappUri: edge.node.webappUri,
    })),
    ...result.data.search.collectors.edges.map(edge => ({
      key: `collector-${edge.node.id}`,
      value: edge.node.displayName || edge.node.publicAddress,
      displayName: edge.node.displayName || edge.node.publicAddress,
      publicAddress: edge.node.publicAddress,
      artistId: null,
      userId: edge.node.id,
      avatarUrl: edge.node.avatar?.url || null,
      webappUri: edge.node.webappUri,
    })),
  ];
}

const MentionsMenu = forwardRef<HTMLUListElement, BeautifulMentionsMenuProps>((props, ref) => {
  const { loading, children, ...rest } = props;
  return !children.length ? null : (
    <ul
      className="pointer-events-auto relative z-above1 mt-8 block w-56 rounded-lg bg-white px-4 pb-2 pt-4 shadow-tertiary"
      {...rest}
      ref={ref}
    >
      <div>{children}</div>
      <div
        className={twMerge(
          'absolute left-0 right-0 top-0 flex h-2 items-center justify-center transition-opacity',
          loading ? 'opacity-100' : 'opacity-0',
        )}
      >
        <div className="loading-container relative h-1 w-36 overflow-hidden rounded-full bg-neutral100">
          <div className="loading-bar absolute bottom-0 left-0 top-0 w-12 animate-loadingBarBounce rounded-full bg-neutral300" />
        </div>
      </div>
    </ul>
  );
});
MentionsMenu.displayName = 'MentionsMenu';

const MentionsMenuItem = forwardRef<
  HTMLLIElement,
  BeautifulMentionsMenuItemProps & CustomMentionNode['data']
>((props, ref) => {
  const {
    selected,
    publicAddress,
    avatarUrl,
    displayName,
    webappUri,
    itemValue: _iv,
    children: _c,
    userId: _uid,
    artistId,
    ...rest
  } = props;

  return (
    <MentionCell
      webappUri={webappUri}
      avatarUrl={avatarUrl}
      publicAddress={publicAddress}
      showVerified={!!artistId}
      selected={selected}
      displayName={displayName}
      ref={ref}
      {...rest}
    />
  );
});
MentionsMenuItem.displayName = 'MentionsMenuItem';

export function MentionsPlugin({
  menuAnchorClassName,
  initialMentionData,
}: {
  menuAnchorClassName?: string;
  initialMentionData?: CustomMentionNode['data'];
}) {
  const queryCache = useRef<{ [query: string]: BeautifulMentionsItem[] }>({});
  const initialFetched = useRef(false);
  const [editor] = useLexicalComposerContext();
  const queryMentions = useCallback(async (_trigger: string, query?: string | null | undefined) => {
    const queryAsString = query || '';
    const cachedSearch = queryCache.current[queryAsString];

    if (cachedSearch) {
      return cachedSearch;
    } else {
      const result = await fetchQuery(MentionableSearchDocument, {
        variables: {
          input: {
            text: query,
          },
        },
      });

      const beautifulMentionItems = dataToBeautifulMentionsItemArray(result);
      queryCache.current[queryAsString] = beautifulMentionItems;
      return beautifulMentionItems;
    }
  }, []);

  /**
   * Fire a prefetch for search right when they start typing
   */
  useEffect(() => {
    return editor.registerUpdateListener(async ({ editorState }) => {
      const count = LexicalUtils.getNodeTextLength(LexicalUtils.getRootNode(editorState));
      if (count >= 1 && !initialFetched.current) {
        initialFetched.current = true;
        const result = await fetchQuery(MentionableSearchDocument, {
          variables: {
            input: {
              text: '',
            },
          },
        });

        const beautifulMentionItems = dataToBeautifulMentionsItemArray(result);

        queryCache.current[''] = beautifulMentionItems;
      }
    });
  }, [editor]);

  /**
   * On select, insert a space after the mention
   */
  const handleMenuItemSelect = useStableCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      const rootChildren = root.getChildren();
      if (rootChildren.length === 1) {
        const textNode = $createTextNode(SPACE);
        rootChildren[0]?.append(textNode);
        textNode.selectNext();
      }
    });
  });

  /**
   * On mount, insert the initial mention data
   */
  useEffectOnce(() => {
    if (initialMentionData) {
      editor.update(() => {
        const root = $getRoot();
        const rootChildren = root.getChildren();
        if (rootChildren.length === 1) {
          const mentionNode = $createBeautifulMentionNode(
            '@',
            initialMentionData.displayName,
            initialMentionData,
          );
          rootChildren[0]?.append(mentionNode);
          const textNode = $createTextNode(SPACE);
          rootChildren[0]?.append(textNode);
          textNode.selectNext();
        }
      });
    }
  });

  return (
    <BeautifulMentionsPlugin
      triggers={['@']}
      onMenuItemSelect={handleMenuItemSelect}
      searchDelay={0}
      menuItemComponent={MentionsMenuItem}
      menuComponent={MentionsMenu}
      onSearch={queryMentions}
      menuAnchorClassName={menuAnchorClassName}
      insertOnBlur={false}
      allowSpaces={false}
    />
  );
}
